import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import TextEditor from 'flarum/common/components/TextEditor';
import ComposerPostPreview from 'flarum/forum/components/ComposerPostPreview';
import Button from 'flarum/common/components/Button';
import Tooltip from 'flarum/common/components/Tooltip';

/**
 * Vista previa del editor, en SPLIT (T-FORO-V14 · punto 4 → PKG-FORO-UX-BUNDLE · frente A).
 *
 * --- POR QUÉ ES SEGURO REUTILIZAR `ComposerPostPreview` ---
 *
 * El core YA tiene el mecanismo: `EditPostComposer`/`ReplyComposer` definen `jumpToPreview()`,
 * que hace scroll al post real de la página y lo sustituye por un `<ComposerPostPreview>`
 * mientras se edita (ver `CommentPost.bodyItems()`). Ese componente sondea
 * `composer.fields.content()` cada 50 ms y llama a `s9e.TextFormatter.preview(texto, dom)` —
 * el MISMO parser/renderer que compone el post de verdad, exportado por
 * `Formatter::getJs()`. No hay Markdown paralelo que mantener ni riesgo de que la vista previa
 * diverja del resultado publicado.
 *
 * El problema de `jumpToPreview` para lo que pide el paquete: sólo existe si hay un post real
 * en la página al que hacer scroll (editar o responder). Un hilo NUEVO (`DiscussionComposer`)
 * no tiene post que sustituir, así que ese botón ni se ofrece ahí — ver `ComposerBody.js`,
 * `preview={this.jumpToPreview?.bind(this)}`, `undefined` si la subclase no lo define.
 *
 * --- QUÉ SE AÑADE, Y POR QUÉ ES ADITIVO ---
 *
 * Un botón propio («Vista previa») en la barra de herramientas de `TextEditor` — la clase base
 * de los tres editores (hilo nuevo, respuesta, edición) — que enciende un panel con el MISMO
 * `<ComposerPostPreview>` del core. No usa `jumpToPreview`, no cambia `composer.editor`, no
 * toca `oninput`/`onsubmit`: si algo sale mal aquí, el usuario sigue pudiendo escribir y enviar
 * exactamente como antes. Apagado por defecto para no sorprender a quien ya conoce el editor.
 *
 * --- EL SPLIT, Y POR QUÉ NO ES EL MISMO LAYOUT EN LOS DOS SITIOS ---
 *
 * Hasta este paquete la previa SUSTITUÍA al editor en las dos anchuras: se veía el render o se
 * veía el texto, nunca los dos. Escribir Markdown a ciegas y alternar para comprobar es
 * exactamente lo que un split evita, así que:
 *
 *   - ESCRITORIO (≥768px): editor y previa lado a lado, 50/50, y la barra de controles a lo
 *     ancho debajo. Se escribe mirando el resultado.
 *   - MÓVIL (<768px): no caben dos columnas legibles a 390px — dos columnas de ~180px parten
 *     cada línea de código y cada tabla. Ahí el par es de PESTAÑAS, «Escribir» / «Vista
 *     previa», que es el patrón que ya conoce cualquiera que haya usado un editor Markdown en
 *     el móvil.
 *
 * POR QUÉ EL SPLIT NO REPITE EL FALLO QUE OBLIGÓ A SUSTITUIR EL EDITOR. Medido en su día con
 * Playwright a 1280x800: con el panel APILADO bajo el textarea, la barra de controles se iba
 * fuera de la pantalla y, como el composer es `position:fixed` al fondo, no había forma de
 * llegar a ella («element is outside of the viewport», literal). En columnas el panel no suma
 * altura: comparte fila con el editor, así que el alto total del composer es el de antes.
 *
 * --- LOS TRES ARREGLOS DE PKG-FORO-SMOKE-FIX, QUE SIGUEN VIGENTES ---
 *
 * (a) SE QUITA EL OJO DEL CORE. Medido en el banco sobre un composer de RESPUESTA: en la barra
 *     hay DOS botones con el mismo icono `far fa-eye` —el del core (`controlItems`, al lado de
 *     Enviar, que minimiza el composer y salta al post) y el nuestro—, indistinguibles a la
 *     vista. Quien pulsa el del core no ve cambiar el cuerpo del editor, que es exactamente el
 *     síntoma reportado en vivo («botón ojo, el cuerpo sigue en Markdown crudo»). Con una sola
 *     vista previa en el composer, la del core sobra.
 *
 * (b) QUIEN DECIDE EL LAYOUT ES EL JS, NO EL MOTOR DE CSS. Antes la hoja escondía el textarea
 *     con `.TextEditor:has(.CmEditorPreview)`, es decir: el ÚNICO mecanismo que sustituye el
 *     editor por su render vivía en una capacidad del motor de CSS, y una hoja vieja en caché o
 *     un motor sin `:has()` dejaban el panel puesto y el textarea también. Ahora la propia
 *     extensión marca la raíz —`TextEditor--cmPreview` y `TextEditor--cmPreviewTab-<pestaña>`—
 *     en la misma pasada en que inserta el panel, y los selectores son descendientes de toda la
 *     vida.
 *
 * (c) EL PANEL SE CIERRA DESDE DENTRO. La barra de herramientas puede quedar fuera del viewport
 *     con el composer alto, así que el propio panel lleva su «Volver a escribir». En móvil ese
 *     botón se oculta a propósito: ahí la salida son las pestañas, y dos controles con el mismo
 *     texto y comportamientos distintos (uno cambia de pestaña, el otro apaga la previa) es
 *     peor que uno solo.
 */

/** Las dos pestañas del móvil. En escritorio no se usa: ahí se ven las dos cosas a la vez. */
type PreviewTab = 'write' | 'preview';

export default function addEditorPreview() {
  extend(TextEditor.prototype, 'oninit', function (this: any) {
    this.cmPreviewOn = false;
    this.cmPreviewTab = 'preview' as PreviewTab;
  });

  /**
   * Fuera el ojo del core (ver (a) arriba).
   *
   * `controlItems()` sólo lo añade cuando `attrs.preview` viene definido, o sea en responder y
   * editar; en un hilo nuevo esta llamada no encuentra nada y no hace nada. `has()` antes de
   * `remove()` porque `ItemList.remove` de una clave ausente es un no-op silencioso hoy, pero
   * preguntarlo deja escrito que la ausencia es un caso esperado y no un descuido.
   */
  extend(TextEditor.prototype, 'controlItems', function (this: any, items: any) {
    if (items.has('preview')) items.remove('preview');
  });

  extend(TextEditor.prototype, 'toolbarItems', function (this: any, items: any) {
    const t = (key: string) => app.translator.trans('umbral-comunidad.forum.editor.' + key);

    items.add(
      'cmPreviewToggle',
      <Tooltip text={t(this.cmPreviewOn ? 'preview_hide' : 'preview_show')}>
        <Button
          icon={this.cmPreviewOn ? 'fas fa-eye-slash' : 'far fa-eye'}
          // `cm-preview-toggle` es el gancho estable para las specs: `toolbarItems()` NO pasa
          // por `listItems()` (ver `TextEditor.js:48`, `{this.toolbarItems().toArray()}` suelto
          // dentro del `<li class="TextEditor-toolbar">`), asi que aqui NO hay `item-<key>` que
          // seleccionar — la leccion de T03B/T11 sobre deducir selectores del JSX.
          className={'Button Button--icon cm-preview-toggle' + (this.cmPreviewOn ? ' active' : '')}
          data-testid="cm-preview-toggle"
          aria-pressed={this.cmPreviewOn ? 'true' : 'false'}
          onclick={() => {
            this.cmPreviewOn = !this.cmPreviewOn;

            // Al encender se entra POR la previa: es lo que se acaba de pedir. En escritorio da
            // igual (se ven las dos), en movil es la diferencia entre ver el render y no ver
            // nada nuevo.
            if (this.cmPreviewOn) this.cmPreviewTab = 'preview';
          }}
        />
      </Tooltip>
    );
  });

  // `view()` devuelve el vnode raíz de `.TextEditor` con dos hijos fijos: el contenedor del
  // textarea y la barra de controles (ver `TextEditor.js`). `extend()` no puede cambiar el
  // valor devuelto (ver `common/extend.ts`), pero SÍ deja mutar ese vnode antes de que Mithril
  // lo pinte — mismo patrón ya usado en `collapsibleSidebar.tsx` para `TagLinkButton`.
  extend(TextEditor.prototype, 'view', function (this: any, vnode: any) {
    if (!this.cmPreviewOn || !vnode || !Array.isArray(vnode.children)) return;

    const t = (key: string) => app.translator.trans('umbral-comunidad.forum.editor.' + key);
    const tab: PreviewTab = this.cmPreviewTab === 'write' ? 'write' : 'preview';

    /*
      LAS MARCAS VAN EN LA RAÍZ, y son lo que decide el layout (ver (b) arriba).

      `vnode.attrs` es el objeto que `view()` acaba de construir en esta misma pasada, así que
      escribir aquí es escribir en algo que todavía no ha llegado al DOM: Mithril lee
      `attrs.className` cuando pinta, después de este extend. No se pisa lo que hubiera: se
      concatena, porque el core ya trae `TextEditor` ahí y otras extensiones pueden añadir lo
      suyo.

      El `data-cm-preview` sale de la MISMA variable que la clase, en la misma línea: es un
      gancho de test, no una segunda fuente de verdad.
    */
    if (vnode.attrs) {
      vnode.attrs.className = (
        (vnode.attrs.className || '') + ' TextEditor--cmPreview TextEditor--cmPreviewTab-' + tab
      ).trim();
      vnode.attrs['data-cm-preview'] = tab;
    }

    /*
      EL ORDEN DE ESTOS DOS HIJOS NO ES ESTETICO: ES LO QUE SALVA EL TEXTAREA.

      Mithril diferencia listas SIN CLAVE POR INDICE. Los hijos reales de `.TextEditor` son dos
      —`div.TextEditor-editorContainer` y `ul.TextEditor-controls`—, y el textarea NO esta en el
      arbol de Mithril: lo cuelga del contenedor el `EditorDriver` en `oncreate`, o sea que para
      Mithril ese `div` esta vacio y sus hijos de verdad son intocables… mientras el `div` siga
      cayendo en el indice 0.

      La primera version metia las pestañas con `unshift`, delante de todo. Resultado medido en
      el DOM real: en el indice 0 Mithril comparaba el `div` viejo (el del textarea) con el
      `div` nuevo (las pestañas), veia la MISMA etiqueta, lo parcheaba en sitio… y el textarea
      se quedaba dentro de la barra de pestañas. Sintoma: `.TextEditor-editorContainer` con
      `edChildren: []` y altura 0 — la persona pulsa «Escribir» y no hay donde escribir.

      Insertando en el indice 1 el contenedor del editor no se mueve nunca, y lo que cae en ese
      indice es el `ul` de controles frente a un `div`: etiquetas distintas, Mithril lo
      reemplaza, y ahi no hay nada que preservar.

      Las pestañas van, pues, DESPUES del editor en el DOM y ANTES por CSS (`order:-1`, solo en
      movil, que es donde se pintan).
    */
    vnode.children.splice(
      1,
      0,
      /*
        Las pestañas viven FUERA del panel: en móvil tienen que poder devolver al editor cuando
        el panel está oculto, y un control que vive dentro de lo que oculta no puede hacerlo.

        `role="tablist"` + `aria-selected` y no dos botones sueltos: para un lector de pantalla
        esto es un selector de vista, y sin el rol se anuncian como dos acciones sin relación.
      */
      <div className="CmEditorPreview-tabs" role="tablist" data-testid="cm-preview-tabs">
        {(['write', 'preview'] as PreviewTab[]).map((name) => (
          <button
            type="button"
            role="tab"
            className={'CmEditorPreview-tab' + (tab === name ? ' is-on' : '')}
            data-cm-preview-tab={name}
            aria-selected={tab === name ? 'true' : 'false'}
            onclick={() => {
              this.cmPreviewTab = name;
            }}
          >
            {t(name === 'write' ? 'tab_write' : 'tab_preview')}
          </button>
        ))}
      </div>,
      <div className="CmEditorPreview-panel" data-testid="cm-preview-panel">
        <div className="CmEditorPreview-bar">
          <span className="CmEditorPreview-label">{t('preview_title')}</span>
          <Button
            className="Button Button--text cm-preview-close"
            icon="fas fa-pen"
            onclick={() => {
              this.cmPreviewOn = false;
            }}
          >
            {t('preview_back')}
          </Button>
        </div>
        {/*
          `Post-body` a propósito: es la clase con la que el foro estila un post publicado
          (párrafos, citas, listas, imágenes). Así lo que se ve aquí no se parece a lo publicado
          — es lo publicado, con el mismo CSS y el mismo formateador.

          LÍMITE CONOCIDO, medido y no supuesto: lo que el parser del foro no convierte tampoco
          se convierte aquí. `![alt](url)` sale como `<img>` y una URL suelta como `<a>`, pero
          un enlace SIN texto (`[](https://)`, que es lo que deja el botón de enlace de la barra
          si no hay nada seleccionado) se queda literal — y se queda igual de literal en el post
          publicado. La vista previa no está fallando: está diciendo la verdad sobre ese enlace.
          Lo cubre `t15.spec.ts`, comparando el HTML de la previa con el del post ya publicado.
        */}
        <ComposerPostPreview className="CmEditorPreview Post-body" composer={this.attrs.composer} />
      </div>
    );
  });
}
